import { useEffect, useMemo, useState } from "react";
import { Eye, LoaderCircle } from "lucide-react";
import { api } from "./api";
import { IntroScene } from "./components/IntroScene";
import { LetterExperience } from "./components/LetterExperience";
import { LightPortal } from "./components/LightPortal";
import { LoginScreen } from "./components/LoginScreen";
import { StatusScreen } from "./components/StatusScreen";
import type { DayContent, ExperiencePayload } from "./types";

function CountdownScreen({ startsAt }: { startsAt: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = Math.max(0, new Date(startsAt).getTime() - now);
  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining / 3_600_000) % 24);
  const minutes = Math.floor((remaining / 60_000) % 60);
  const seconds = Math.floor((remaining / 1000) % 60);
  return (
    <main className="countdown-screen">
      <div>
        <h1>הסיפור שלנו מתחיל בקרוב</h1>
        <p>הפרק הראשון ייפתח ביום שני, 23.11.2026 בשעה 06:00.</p>
        <dl>
          <div><dt>ימים</dt><dd>{days}</dd></div>
          <div><dt>שעות</dt><dd>{hours}</dd></div>
          <div><dt>דקות</dt><dd>{minutes}</dd></div>
          <div><dt>שניות</dt><dd>{seconds}</dd></div>
        </dl>
      </div>
    </main>
  );
}

function EndScreen() {
  return (
    <main className="end-screen">
      <div>
        <p>02.12.2026</p>
        <h1>ומכאן מתחיל הסיפור האמיתי שלנו</h1>
        <p>עשרה ימים, עשרה מכתבים, ואהבה אחת שממשיכה לכל החיים.</p>
        <strong>אבי ורבקה</strong>
      </div>
    </main>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [experience, setExperience] = useState<ExperiencePayload | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [introStep, setIntroStep] = useState(0);
  const [musicReady, setMusicReady] = useState(false);

  const statusRoute = window.location.pathname === "/opens";

  useEffect(() => {
    if (statusRoute) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    api.logout().finally(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      setAuthenticated(false);
    });
  }, [statusRoute]);

  useEffect(() => {
    if (!authenticated) return;
    api.experience().then((payload) => {
      setExperience(payload);
      const firstAvailable = payload.days.find((day) => !day.locked);
      if (firstAvailable) setSelectedId(firstAvailable.id);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
      });
    });
  }, [authenticated]);

  useEffect(() => {
    const storyIsActive = Boolean(
      authenticated
      && experience
      && (experience.phase === "preview" || experience.phase === "live")
      && !musicReady,
    );
    document.body.classList.toggle("story-locked", storyIsActive);
    return () => {
      document.body.classList.remove("story-locked");
      document.body.classList.remove("story-transitioning");
    };
  }, [authenticated, experience, musicReady]);

  const selectedDay = useMemo(
    () => experience?.days.find((day) => day.id === selectedId) ?? null,
    [experience, selectedId],
  );

  async function login(code: string) {
    await api.login(code);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setAuthenticated(true);
  }

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }

  function continueToLight() {
    const target = document.getElementById("light-portal");
    if (!target) return;
    document.body.classList.remove("story-locked");
    document.body.classList.add("story-transitioning");
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth" });
      window.setTimeout(() => {
        document.body.classList.remove("story-transitioning");
        document.body.classList.add("story-locked");
      }, 900);
    });
  }

  function enterLight() {
    document.body.classList.remove("story-locked");
    document.body.classList.remove("story-transitioning");
    setMusicReady(true);
    window.requestAnimationFrame(() => scrollTo("daily-letter"));
  }

  function selectDay(day: DayContent) {
    if (day.locked) return;
    setSelectedId(day.id);
    setIntroStep(2);
    scrollTo("daily-letter");
  }

  if (statusRoute) return <StatusScreen />;

  if (authenticated === null) {
    return <main className="loading-screen"><LoaderCircle className="spinner" aria-hidden="true" /><span>טוען את הסיפור…</span></main>;
  }

  if (!authenticated) return <LoginScreen onLogin={login} />;
  if (!experience) return <main className="loading-screen"><LoaderCircle className="spinner" aria-hidden="true" /><span>פותח את המכתבים…</span></main>;
  if (experience.phase === "before") return <CountdownScreen startsAt={experience.startsAt} />;
  if (experience.phase === "ended") return <EndScreen />;
  if (!selectedDay) return <main className="loading-screen">אין עדיין פרק זמין.</main>;

  return (
    <main>
      {experience.preview && (
        <div className="preview-banner">
          <Eye aria-hidden="true" />
          <span>מצב תצוגה — כל עשרת הימים פתוחים</span>
        </div>
      )}
      <IntroScene
        day={selectedDay}
        step={introStep}
        onAdvance={() => setIntroStep(2)}
        onContinue={continueToLight}
      />
      <LightPortal prompt={selectedDay.lightPrompt} onEnter={enterLight} />
      <LetterExperience
        day={selectedDay}
        days={experience.days}
        musicReady={musicReady}
        onSelectDay={selectDay}
        onOpened={(dayId) => void api.recordOpen(dayId)}
      />
    </main>
  );
}
